import { useParams, useNavigate, Link } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  GripVertical,
  DollarSign,
  Calendar,
  User,
  Plus,
  Pencil,
  Trash2,
  X,
  Package,
  FileDown,
  Eye,
  Copy,
  Edit,
} from 'lucide-react'
import { GRACKLE_COLORS, SEMANTIC } from '@/lib/theme'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toDisplayDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { NoPriceLabel } from '@/components/NoPriceLabel'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { EditActionBar } from '@/components/EditActionBar'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import { apiClient } from '@/lib/api'
import { HistoryTimeline } from '@/components/HistoryTimeline'
import { AttachmentsTab } from '@/components/AttachmentsTab'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useQuote,
  useUpdateQuote,
  useQuoteLineItems,
  useCreateQuoteLineItem,
  useUpdateQuoteLineItem,
  useDeleteQuoteLineItem,
  useReorderQuoteLineItems,
  useProducts,
  useRequirePricedProducts,
  useDownloadQuotePdf,
  useCloneQuote,
  useUsers,
  useOpportunities,
  useAccounts,
  useContacts,
  useEntityContent,
} from '@/hooks/use-api'
import type { Quote, QuoteLineItem, QuoteRegion } from '@/types'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { QuoteStageProgress } from '@/components/QuoteStageProgress'
import { useUser } from '@/contexts/UserContext'
import {
  matchProductFamily,
  REQUIRED_FIELDS_BY_FAMILY,
  DOMAIN_USERS_LABEL_BY_FAMILY,
  SUBSCRIPTION_TERM_OPTIONS,
  validateLineItemExtras,
  validatePdfUserEmails,
  parseEmailList,
} from '@/lib/productFamilyRules'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { explainError } from '@/lib/explain-error'
import {
  QUOTE_REGION_OPTIONS,
  QUOTE_REGION_AUTO_LABEL,
  quoteRegionLabel,
  expectedCurrencyForRegion,
  isRegionCurrencyMismatch,
  regionCurrencyWarning,
} from '@/lib/quoteRegions'

// ---------------------------------------------------------------------------
// Sortable table row for quote line items
// ---------------------------------------------------------------------------
interface SortableLineItemRowProps {
  item: any
  index: number
  totalItems: number
  isLocked: boolean
  isAddingLineItem: boolean
  reorderPending: boolean
  deletePending: boolean
  formatCurrency: (v: number) => string
  getLineItemTotal: (item: any) => number
  onMoveUp: () => void
  onMoveDown: () => void
  onEdit: () => void
  onDelete: () => void
}

function SortableLineItemRow({
  item,
  index,
  totalItems,
  isLocked,
  isAddingLineItem,
  reorderPending,
  deletePending,
  formatCurrency,
  getLineItemTotal,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
}: SortableLineItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: isLocked })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 1 : undefined,
  }

  return (
    <tr ref={setNodeRef} style={style} {...attributes} className="border-t">
      {totalItems > 1 && (
        <td className="p-3">
          <div className="flex items-center justify-center gap-0.5">
            {/* Drag handle */}
            {!isLocked && (
              <button
                ref={setActivatorNodeRef}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Drag to reorder ${item.product_name || 'item'}`}
                tabIndex={0}
              >
                <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
            {/* Up / down buttons (kept for accessibility and user familiarity) */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onMoveUp}
              disabled={index === 0 || reorderPending || isLocked}
              aria-label={`Move ${item.product_name || 'item'} up`}
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onMoveDown}
              disabled={index === totalItems - 1 || reorderPending || isLocked}
              aria-label={`Move ${item.product_name || 'item'} down`}
            >
              <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </td>
      )}
      <td className="p-3">
        <div>
          <div className="font-medium">{item.product_name || 'Unknown Product'}</div>
          {item.product_code && (
            <div className="text-xs text-muted-foreground">{item.product_code}</div>
          )}
        </div>
      </td>
      <td className="text-right p-3">{item.quantity}</td>
      <td className="text-right p-3">{formatCurrency(item.unit_price)}</td>
      <td className="text-right p-3">
        {item.discount ? `-${formatCurrency(item.discount)}` : '—'}
      </td>
      <td className="text-right p-3 font-medium">{formatCurrency(getLineItemTotal(item))}</td>
      <td className="text-right p-3">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            disabled={isAddingLineItem || isLocked}
            aria-label={`Edit line item for ${item.product_name}`}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={deletePending || isLocked}
            aria-label={`Delete line item for ${item.product_name}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </td>
    </tr>
  )
}

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { currentUser } = useUser()

  const { data: quote, isLoading, error } = useQuote(id!)
  // Won quotes (Accepted/Processed) are read-only: pricing and line items must not change.
  const isLocked = ['Accepted', 'Processed'].includes(quote?.status || '')
  useDocumentTitle(
    quote ? `${quote.name || `Quote ${quote.quote_number ?? ''}`.trim()} | Quote` : undefined
  )
  useRecordBreadcrumb(quote ? quote.name || `Quote ${quote.quote_number ?? ''}`.trim() : undefined)
  const { data: lineItemsData, isLoading: lineItemsLoading } = useQuoteLineItems(id!)
  // Resolve product prices in THIS quote's currency. Without the parameter the
  // API returns whatever the corporate-currency default resolves to, which the
  // form then labelled with the quote's currency — a mislabelled number, not a
  // converted one.
  // Governance switch (off by default) plus its escape hatch — see
  // backend/src/services/pricingSettings.js.
  const requirePriced = useRequirePricedProducts()
  const [showUnpriced, setShowUnpriced] = useState(false)
  const pricedOnly = requirePriced && !showUnpriced

  const { data: productsData } = useProducts({
    limit: 10000,
    currency: quote?.currency_iso_code || undefined,
    priced_only: pricedOnly || undefined,
  })
  const { data: usersData } = useUsers({ limit: 10000 })
  const { data: opportunitiesData } = useOpportunities({ limit: 10000 })
  const { data: accountsData } = useAccounts({ limit: 10000 })
  const { data: filesData } = useEntityContent(id!)
  const files = filesData?.data ?? []

  const updateQuoteMutation = useUpdateQuote()
  const createLineItemMutation = useCreateQuoteLineItem()
  const updateLineItemMutation = useUpdateQuoteLineItem()
  const deleteLineItemMutation = useDeleteQuoteLineItem()
  const reorderLineItemsMutation = useReorderQuoteLineItems(id!)
  const downloadPdfMutation = useDownloadQuotePdf()
  const cloneQuoteMutation = useCloneQuote()

  // Drag-and-drop sensors: require 8px movement before activating so clicks still fire
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false)
  const [editForm, setEditForm] = useState<Partial<Quote>>({})
  const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({})
  // Currency should match the issuing entity; a deliberate mismatch needs an
  // explicit acknowledgement before the edit can be saved.
  const [currencyMismatchAck, setCurrencyMismatchAck] = useState(false)
  const regionCurrencyMismatch = isRegionCurrencyMismatch(
    editForm.region,
    editForm.currency_iso_code
  )

  // Click-to-edit — must be called before any conditional returns. The
  // lambda reads `handleStartEdit` lexically when the user clicks (after
  // render, so the function is defined).
  const { bindReadOnly } = useClickToEdit(isEditMode, () => handleStartEdit())

  // Per-render PDF template override. 'auto' = use the quote's saved Issuing
  // Region, falling back to the bill-to country. Changing this affects only the
  // PDF you're about to open/download; the saved region is the durable setting.
  const [pdfTemplate, setPdfTemplate] = useState<'auto' | QuoteRegion>('auto')

  // Line item form state
  const [isAddingLineItem, setIsAddingLineItem] = useState(false)
  const [editingLineItemId, setEditingLineItemId] = useState<string | null>(null)
  const [lineItemForm, setLineItemForm] = useState<Partial<QuoteLineItem>>({
    product_id: '',
    quantity: 1,
    unit_price: 0,
    discount: 0,
    domain_users_text: null,
    subscription_term_months: null,
    monthly_delivery_day: null,
    primary_contact_id: null,
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const formatCurrency = (amount?: number) => {
    const currency = quote?.currency_iso_code || 'USD'
    const value = amount || 0
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value)
    } catch {
      // Intl throws on invalid/junk ISO codes — fall back to numeric + code.
      return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${currency}`
    }
  }

  const formatDate = (date?: string) => {
    const d = toDisplayDate(date)
    if (!d) return 'N/A'
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (date?: string) => {
    if (!date) return 'N/A'
    return (toDisplayDate(date) ?? new Date(date)).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Handle stage change from progress indicator
  const handleStageChange = async (newStage: string) => {
    try {
      await updateQuoteMutation.mutateAsync({
        id: id!,
        data: { status: newStage },
      })
      toast({
        title: 'Status Updated',
        description: `Quote status changed to ${newStage}.`,
      })
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update status')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Related data arrays
  const users = usersData?.data ?? []
  const opportunities = opportunitiesData?.data ?? []
  const accounts = accountsData?.data ?? []

  // Fetch contacts for the quote's account (for primary_contact_id)
  const { data: contactsData } = useContacts({
    account_id: quote?.account_id || undefined,
    limit: 10000,
    sort_by: 'last_name',
    sort_order: 'asc',
  })
  const accountContacts = contactsData?.data || []

  // Edit mode handlers
  const handleStartEdit = () => {
    if (!quote) return
    if (isLocked) return
    setEditForm({
      name: quote.name || '',
      status: quote.status || 'Draft',
      opportunity_id: quote.opportunity_id || '',
      account_id: quote.account_id || '',
      owner_id: quote.owner_id || currentUser?.id || '',
      expiration_date: quote.expiration_date ? quote.expiration_date.split('T')[0] : '',
      description: quote.description || '',
      currency_iso_code: quote.currency_iso_code || 'USD',
      region: quote.region || '',
    })
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setCurrencyMismatchAck(false)
    setIsEditMode(false)
    setEditForm({})
    setEditFormErrors({})
  }

  const handleSaveEdit = async () => {
    if (regionCurrencyMismatch && !currencyMismatchAck) {
      toast({
        title: 'Confirm the currency override',
        description:
          'The quote currency does not match the issuing region. Tick the confirmation box to continue.',
        variant: 'destructive',
      })
      return
    }

    // Validate required fields
    const errors: Record<string, string> = {}
    if (!editForm.name?.trim()) {
      errors.name = 'Quote name is required'
    }
    if (!editForm.owner_id) {
      errors.owner_id = 'Owner is required'
    }

    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors)
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    try {
      await updateQuoteMutation.mutateAsync({
        id: id!,
        data: editForm,
      })
      toast({
        title: 'Success',
        description: 'Quote updated successfully.',
      })
      setIsEditMode(false)
      setEditForm({})
      setEditFormErrors({})
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update quote')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleEditFieldChange = (field: string, value: any) => {
    setEditForm((prev) => {
      const next = { ...prev, [field]: value }
      // Choosing a region moves the currency to that entity's default; the
      // user can override afterwards and acknowledge the mismatch.
      if (field === 'region') {
        const expected = expectedCurrencyForRegion(value)
        if (expected) next.currency_iso_code = expected
      }
      return next
    })
    if (field === 'region' || field === 'currency_iso_code') setCurrencyMismatchAck(false)
    // Clear error when field is modified
    if (editFormErrors[field]) {
      setEditFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Render editable field helper
  const renderEditableField = (
    label: string,
    field: string,
    value: any,
    type: 'text' | 'date' | 'select' | 'textarea' | 'owner' | 'opportunity' | 'account' = 'text',
    // Plain values, or {value,label} pairs when the stored value isn't the
    // text we want to show the user (e.g. region keys like 'usa').
    options?: ReadonlyArray<string | { value: string; label: string }>,
    required?: boolean,
    // Label for the empty option. Defaults to 'Select...'; fields where blank
    // has a meaning (e.g. Region = auto) should say what that meaning is.
    // Pass null to omit the empty option entirely, for a field that must always
    // hold a value — see Issuing Region, where offering blank to a quote that has
    // a region would let the user un-set the entity printed on the PDF.
    emptyOptionLabel: string | null = 'Select...'
  ) => {
    if (isEditMode) {
      const fieldValue = editForm[field as keyof typeof editForm]
      return (
        <div className="py-2 border-b border-border">
          <dt className="text-sm font-medium text-muted-foreground">
            {label}
            {required && ' *'}
          </dt>
          <dd className="mt-0.5">
            {type === 'textarea' ? (
              <textarea
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              />
            ) : type === 'select' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              >
                {emptyOptionLabel !== null && <option value="">{emptyOptionLabel}</option>}
                {options?.map((opt) => {
                  const value = typeof opt === 'string' ? opt : opt.value
                  const optLabel = typeof opt === 'string' ? opt : opt.label
                  return (
                    <option key={value} value={value}>
                      {optLabel}
                    </option>
                  )
                })}
              </select>
            ) : type === 'owner' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              >
                <option value="">Select owner...</option>
                {[...users]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
              </select>
            ) : type === 'opportunity' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              >
                <option value="">Select opportunity...</option>
                {[...opportunities]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((opp) => (
                    <option key={opp.id} value={opp.id}>
                      {opp.name}
                    </option>
                  ))}
              </select>
            ) : type === 'account' ? (
              <select
                id={field}
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              >
                <option value="">Select account...</option>
                {[...accounts]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
              </select>
            ) : type === 'date' ? (
              <DatePicker
                id={field}
                value={fieldValue as string}
                onChange={(v) => handleEditFieldChange(field, v ?? '')}
                className="h-9"
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              />
            ) : (
              <Input
                id={field}
                type="text"
                value={(fieldValue as string) || ''}
                onChange={(e) => handleEditFieldChange(field, e.target.value)}
                className="h-9"
                aria-required={required}
                aria-invalid={!!editFormErrors[field]}
                aria-describedby={editFormErrors[field] ? `${field}-error` : undefined}
              />
            )}
            {editFormErrors[field] && (
              <p id={`${field}-error`} className="text-sm text-destructive mt-1" role="alert">
                {editFormErrors[field]}
              </p>
            )}
          </dd>
        </div>
      )
    }
    // Display mode — clicking the value drops the page into edit mode
    // and focuses this field's input.
    const readProps = bindReadOnly(field, label)
    return (
      <div className="py-2 border-b border-border">
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd
          {...readProps}
          className={`mt-0.5 text-sm flex items-center justify-between gap-2 ${readProps.className}`}
        >
          <span className="min-w-0 break-words whitespace-pre-wrap">{value}</span>
          <Pencil className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
        </dd>
      </div>
    )
  }

  // Line item data (must be before conditional returns due to React hooks)
  const lineItems = lineItemsData || []
  const availableProducts = productsData?.data || []

  // The currency every price on this quote is expressed in.
  const quoteCurrency = quote?.currency_iso_code || 'USD'

  // The selected product WHEN it has no list price in the quote's currency.
  // Undefined otherwise, so it doubles as the "should we warn?" flag.
  const unpricedSelection = useMemo(() => {
    if (!lineItemForm.product_id) return undefined
    const product = availableProducts.find((p) => p.id === lineItemForm.product_id)
    return product && !product.unit_price ? product : undefined
  }, [availableProducts, lineItemForm.product_id])

  // Calculate line item totals (discount is a dollar amount)
  const calculateLineItemTotal = (quantity: number, unitPrice: number, discount: number = 0) => {
    const subtotal = quantity * unitPrice
    return subtotal - discount
  }

  // Get line item total (handles NULL total_price by calculating from quantity × unit_price)
  const getLineItemTotal = (item: QuoteLineItem) => {
    if (item.total_price != null) return item.total_price
    const subtotal = (item.quantity || 0) * (item.unit_price || 0)
    return item.discount ? subtotal - item.discount : subtotal
  }

  const lineItemsSubtotal = lineItems.reduce((sum, item) => sum + getLineItemTotal(item), 0)

  // Auto-sync quote total when line items change
  useEffect(() => {
    // Guard: only execute when data is ready and line items exist.
    // Skip locked (won) quotes — they are read-only, and the backend rejects any
    // non-status edit, so attempting the auto-sync would 403 on every view.
    if (!quote || !lineItemsData || isLoading || lineItems.length === 0 || !id || isLocked) {
      return
    }

    const currentTotal = quote.total_price || 0
    const calculatedTotal = lineItemsSubtotal

    // Only update if there's a difference (with tolerance for floating point)
    if (Math.abs(currentTotal - calculatedTotal) > 0.01) {
      updateQuoteMutation.mutate({
        id,
        data: { total_price: calculatedTotal },
      })
    }
  }, [lineItemsSubtotal, lineItems.length, quote, lineItemsData, isLoading, id, isLocked])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading quote details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading quote details...</p>
        </div>
      </div>
    )
  }

  if (error || !quote) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading quote</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Quote not found')}
        </p>
        <Button
          onClick={() => navigate('/quotes')}
          className="mt-4"
          aria-label="Return to quotes list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Quotes
        </Button>
      </div>
    )
  }

  // Line item form handlers
  const resetLineItemForm = () => {
    setLineItemForm({
      product_id: '',
      quantity: 1,
      unit_price: 0,
      discount: 0,
      domain_users_text: null,
      subscription_term_months: null,
      monthly_delivery_day: null,
      primary_contact_id: null,
    })
    setFormErrors({})
    setIsAddingLineItem(false)
    setEditingLineItemId(null)
  }

  const handleProductSelect = (productId: string) => {
    const product = availableProducts.find((p) => p.id === productId)
    if (product) {
      // Clear all form errors when selecting a new product to start fresh
      setFormErrors({})
      setLineItemForm((prev) => ({
        ...prev,
        product_id: productId,
        unit_price: product.unit_price || 0,
        // Clear product-specific fields when switching products to prevent cross-contamination
        domain_users_text: null,
        subscription_term_months: null,
        monthly_delivery_day: null,
        primary_contact_id: null,
      }))
    }
  }

  const validateLineItemForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!lineItemForm.quantity || lineItemForm.quantity <= 0) {
      errors.quantity = 'Quantity must be greater than 0'
    }

    if (lineItemForm.unit_price === undefined || lineItemForm.unit_price < 0) {
      errors.unit_price = 'Unit price must be 0 or greater'
    }

    if (lineItemForm.discount !== undefined && lineItemForm.discount < 0) {
      errors.discount = 'Discount must be 0 or greater'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveLineItem = async () => {
    if (!validateLineItemForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form',
        variant: 'destructive',
      })
      return
    }

    // Confirm a zero-cost line before it reaches a customer-facing quote.
    // 97 line items have already gone out at 0.00 with nothing asking. Confirm
    // rather than block — free trials and bundled items are legitimate.
    if (!(Number(lineItemForm.unit_price) > 0)) {
      const ok = window.confirm(
        'This line has a price of 0.00, so the customer will see a zero-cost ' +
          'item on the quote.\n\nAdd it anyway?'
      )
      if (!ok) return
    }

    // Client-side validation for product-family-specific required fields
    const selectedProduct = availableProducts.find((p) => p.id === lineItemForm.product_id)
    if (selectedProduct) {
      const { familyKey, missing } = validateLineItemExtras(
        { name: selectedProduct.name, family: selectedProduct.family },
        {
          domain_users_text: lineItemForm.domain_users_text,
          subscription_term_months: lineItemForm.subscription_term_months,
          monthly_delivery_day: lineItemForm.monthly_delivery_day,
          primary_contact_id: lineItemForm.primary_contact_id,
        }
      )
      if (missing.length > 0) {
        const labelMap: Record<string, string> = {
          domain_users_text: familyKey
            ? `${DOMAIN_USERS_LABEL_BY_FAMILY[familyKey]} is required`
            : 'Required',
          subscription_term_months: 'Subscription term is required',
          monthly_delivery_day: 'Monthly delivery date is required',
          primary_contact_id: 'Main point of contact is required',
        }
        const newErrors: Record<string, string> = {}
        for (const f of missing) newErrors[f] = labelMap[f] || 'Required'
        setFormErrors((prev) => ({ ...prev, ...newErrors }))
        toast({
          title: 'Missing required fields',
          description: 'Please fill in all fields marked with *',
          variant: 'destructive',
        })
        return
      }

      // PDF family: User(s) must be a comma-separated list of valid emails.
      const emailCheck = validatePdfUserEmails(
        { name: selectedProduct.name, family: selectedProduct.family },
        { domain_users_text: lineItemForm.domain_users_text }
      )
      if (emailCheck.applies && emailCheck.invalid.length > 0) {
        setFormErrors((prev) => ({
          ...prev,
          domain_users_text: `Invalid email${emailCheck.invalid.length > 1 ? 's' : ''}: ${emailCheck.invalid.join(', ')}`,
        }))
        toast({
          title: 'Invalid email addresses',
          description: emailCheck.invalid.join(', '),
          variant: 'destructive',
        })
        return
      }
    }

    const lineItemData = {
      ...lineItemForm,
      quantity: Number(lineItemForm.quantity),
      unit_price: Number(lineItemForm.unit_price),
      discount: Number(lineItemForm.discount || 0),
      domain_users_text: lineItemForm.domain_users_text,
      subscription_term_months: lineItemForm.subscription_term_months,
      monthly_delivery_day: lineItemForm.monthly_delivery_day,
      primary_contact_id: lineItemForm.primary_contact_id,
    }

    try {
      if (editingLineItemId) {
        await updateLineItemMutation.mutateAsync({
          quoteId: id!,
          id: editingLineItemId,
          data: lineItemData,
        })
        toast({
          title: 'Success',
          description: 'Line item updated successfully',
        })
      } else {
        await createLineItemMutation.mutateAsync({
          quoteId: id!,
          data: lineItemData,
        })
        toast({
          title: 'Success',
          description: 'Line item added successfully',
        })
      }
      // Only reset form on SUCCESS — do not reset if validation fails
      resetLineItemForm()
    } catch (error) {
      // Handle backend 400 error with missing_fields or invalid_emails
      const axiosError = error as {
        response?: {
          data?: {
            error?: string
            missing_fields?: string[]
            invalid_emails?: string[]
            field?: string
          }
        }
      }
      const invalidEmails = axiosError?.response?.data?.invalid_emails
      if (invalidEmails && invalidEmails.length > 0) {
        setFormErrors((prev) => ({
          ...prev,
          domain_users_text: `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`,
        }))
        toast({
          title: 'Invalid email addresses',
          description: invalidEmails.join(', '),
          variant: 'destructive',
        })
        return
      }
      const missingFields: string[] | undefined = axiosError?.response?.data?.missing_fields
      if (missingFields && missingFields.length > 0) {
        const labelMap: Record<string, string> = {
          domain_users_text: 'Required',
          subscription_term_months: 'Subscription term is required',
          monthly_delivery_day: 'Monthly delivery day (1-31) is required for Scan products',
          primary_contact_id: 'Main point of contact is required',
        }
        const newErrors: Record<string, string> = {}
        for (const f of missingFields) newErrors[f] = labelMap[f] || 'Required'
        setFormErrors((prev) => ({ ...prev, ...newErrors }))
        toast({
          title: 'Missing required fields',
          description: missingFields.join(', '),
          variant: 'destructive',
        })
        return
      }
      // Generic error fallback
      const errorMsg =
        axiosError?.response?.data?.error || explainError(error, 'Failed to save line item')
      console.error('Error saving line item:', errorMsg, { formData: lineItemData })
      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive',
      })
    }
  }

  const handleEditLineItem = (item: QuoteLineItem) => {
    setLineItemForm({
      product_id: item.product_id || '',
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount: item.discount || 0,
      description: item.description || '',
      domain_users_text: item.domain_users_text ?? null,
      subscription_term_months: item.subscription_term_months ?? null,
      monthly_delivery_day: item.monthly_delivery_day ?? null,
      primary_contact_id: item.primary_contact_id ?? null,
    })
    setEditingLineItemId(item.id)
    setIsAddingLineItem(true)
  }

  const handleDeleteLineItem = async (itemId: string) => {
    if (!confirm('Are you sure you want to delete this line item?')) {
      return
    }

    try {
      await deleteLineItemMutation.mutateAsync({
        quoteId: id!,
        id: itemId,
      })
      toast({
        title: 'Success',
        description: 'Line item deleted successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to delete line item'),
        variant: 'destructive',
      })
    }
  }

  const handleMoveLineItem = async (index: number, direction: 'up' | 'down') => {
    if (!lineItems) return
    const swapIdx = direction === 'up' ? index - 1 : index + 1
    if (swapIdx < 0 || swapIdx >= lineItems.length) return

    const order = lineItems.map((item, i) => {
      if (i === index) return { id: item.id, sort_order: swapIdx }
      if (i === swapIdx) return { id: item.id, sort_order: index }
      return { id: item.id, sort_order: i }
    })

    try {
      await reorderLineItemsMutation.mutateAsync(order)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to reorder line items.',
        variant: 'destructive',
      })
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !lineItems) return

    const oldIndex = lineItems.findIndex((item) => item.id === active.id)
    const newIndex = lineItems.findIndex((item) => item.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    // Build new order with every item reassigned a sequential sort_order
    const reordered = [...lineItems]
    const moved = reordered.splice(oldIndex, 1)[0]
    if (!moved) return
    reordered.splice(newIndex, 0, moved)
    const order = reordered.map((item, i) => ({ id: item.id, sort_order: i }))

    try {
      await reorderLineItemsMutation.mutateAsync(order)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to reorder line items.',
        variant: 'destructive',
      })
    }
  }

  const handleViewPdf = async () => {
    if (!id) return

    try {
      // Fetch PDF via authenticated Axios client then open as blob URL
      const templateParam = pdfTemplate === 'auto' ? undefined : pdfTemplate
      const { blob, fallback } = await apiClient.downloadQuotePdf(id, templateParam)
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      if (fallback) {
        toast({
          title: 'Accessible PDF unavailable',
          description:
            'The accessible PDF service could not be reached, so a standard (non-accessible) PDF was generated instead.',
          variant: 'destructive',
        })
      }
    } catch (error) {
      toast({
        title: 'Unable to open PDF',
        description: explainError(error, 'Failed to load PDF'),
        variant: 'destructive',
      })
    }
  }

  const handleDownloadPdf = async () => {
    if (!id) return

    try {
      const templateParam = pdfTemplate === 'auto' ? undefined : pdfTemplate
      const result = await downloadPdfMutation.mutateAsync({
        id,
        template: templateParam,
      })
      if (result?.fallback) {
        toast({
          title: 'Accessible PDF unavailable',
          description:
            'The accessible PDF service could not be reached, so a standard (non-accessible) PDF was generated instead.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Success',
          description: 'Quote PDF downloaded successfully',
        })
      }
    } catch (error) {
      toast({
        title: 'Unable to download PDF',
        description: explainError(error, 'Failed to download PDF'),
        variant: 'destructive',
      })
    }
  }

  const handleCloneQuote = async () => {
    if (!id) return

    try {
      const clonedQuote = await cloneQuoteMutation.mutateAsync(id)
      toast({
        title: 'Success',
        description: 'Quote cloned successfully',
      })
      navigate(`/quotes/${clonedQuote.id}`)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to clone quote'),
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6" role="main" aria-label="Quote details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/quotes')}
            aria-label="Back to quotes list"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <CategoryBadge type="quote" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{quote.name || 'Quote'}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Quote #{quote.quote_number || 'N/A'}
              {quote.opportunity_name && (
                <>
                  {' • '}
                  {quote.opportunity_id ? (
                    <Link to={`/opportunities/${quote.opportunity_id}`} className="hover:underline">
                      {quote.opportunity_name}
                    </Link>
                  ) : (
                    quote.opportunity_name
                  )}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-0.5">
            <Label
              htmlFor="pdf-template-select"
              className="text-[11px] text-muted-foreground leading-none"
            >
              PDF template
            </Label>
            <Select
              value={pdfTemplate}
              onValueChange={(v) => setPdfTemplate(v as 'auto' | QuoteRegion)}
            >
              <SelectTrigger
                id="pdf-template-select"
                className="h-8 w-[190px]"
                aria-label="Choose PDF template"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">
                  {quote.region ? `Use quote region (${quote.region})` : 'Auto (by country)'}
                </SelectItem>
                {QUOTE_REGION_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewPdf}
            aria-label="View quote PDF in browser"
          >
            <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
            View PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={downloadPdfMutation.isPending}
            aria-label="Download quote as PDF"
          >
            <FileDown className="mr-2 h-4 w-4" aria-hidden="true" />
            {downloadPdfMutation.isPending ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCloneQuote}
            disabled={cloneQuoteMutation.isPending || isEditMode}
            aria-label="Clone quote with all line items"
          >
            <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
            {cloneQuoteMutation.isPending ? 'Cloning...' : 'Clone Quote'}
          </Button>
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                aria-label="Cancel editing"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={updateQuoteMutation.isPending}
                aria-label="Save quote changes"
              >
                {updateQuoteMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartEdit}
              disabled={isLocked}
              title={
                isLocked
                  ? `This quote is ${quote.status} and is read-only. Change its status to edit.`
                  : undefined
              }
              aria-label="Edit quote details"
            >
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit Quote
            </Button>
          )}
        </div>
      </div>

      {/* Stage Progress Indicator */}
      <QuoteStageProgress
        currentStage={quote.status || 'Draft'}
        onStageChange={handleStageChange}
        disabled={updateQuoteMutation.isPending}
      />

      {isLocked && (
        <div
          role="status"
          className="rounded-md border px-4 py-2 text-sm"
          style={{
            backgroundColor: SEMANTIC.warning.fill,
            borderColor: GRACKLE_COLORS.pollen,
            color: SEMANTIC.warning.text,
          }}
        >
          This quote is <span className="font-medium">{quote.status}</span> and is read-only. Change
          its status to edit its details or line items.
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Grand Total</CardTitle>
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatCurrency(
                (quote.total_price || 0) + (quote.tax || 0) + (quote.shipping_handling || 0)
              )}
            </div>
            {lineItems.length > 0 && (
              <p className="text-xs text-muted-foreground leading-tight mt-1">
                Subtotal: {formatCurrency(lineItemsSubtotal)}
                {quote.tax ? ` + Tax: ${formatCurrency(quote.tax)}` : ''}
                {quote.shipping_handling
                  ? ` + S&H: ${formatCurrency(quote.shipping_handling)}`
                  : ''}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Expiration Date</CardTitle>
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatDate(quote.expiration_date)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Owner</CardTitle>
            <User className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-base font-semibold">{quote.owner_name || 'Unassigned'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content with Sidebar Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[13fr_7fr] gap-6">
        {/* Left Column: Main Content */}
        <div className="space-y-6">
          {/* About Section */}
          <Card>
            <CardHeader>
              <CardTitle>About</CardTitle>
              <CardDescription>Key quote information and details</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
                {renderEditableField(
                  'Quote Name',
                  'name',
                  quote.name || 'N/A',
                  'text',
                  undefined,
                  true
                )}

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Quote Number</dt>
                  <dd className="mt-0.5 text-sm">{quote.quote_number || 'N/A'}</dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Total Price</dt>
                  <dd className="mt-0.5 text-sm font-semibold">
                    {formatCurrency(quote.total_price)}
                  </dd>
                </div>

                {/* Opportunity - special handling for link in display mode */}
                {isEditMode
                  ? renderEditableField(
                      'Opportunity',
                      'opportunity_id',
                      quote.opportunity_name || 'N/A',
                      'opportunity'
                    )
                  : (() => {
                      const p = bindReadOnly('opportunity_id', 'Opportunity')
                      return (
                        <div className="py-2 border-b border-border">
                          <dt className="text-sm font-medium text-muted-foreground">Opportunity</dt>
                          <dd {...p} className={`mt-0.5 text-sm ${p.className}`}>
                            {quote.opportunity_id ? (
                              <Link
                                to={`/opportunities/${quote.opportunity_id}`}
                                className="text-primary hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {quote.opportunity_name || 'N/A'}
                              </Link>
                            ) : (
                              quote.opportunity_name || 'N/A'
                            )}
                          </dd>
                        </div>
                      )
                    })()}

                {/* Account - special handling for link in display mode */}
                {isEditMode
                  ? renderEditableField(
                      'Account',
                      'account_id',
                      quote.account_name || 'N/A',
                      'account'
                    )
                  : (() => {
                      const p = bindReadOnly('account_id', 'Account')
                      return (
                        <div className="py-2 border-b border-border">
                          <dt className="text-sm font-medium text-muted-foreground">Account</dt>
                          <dd {...p} className={`mt-0.5 text-sm ${p.className}`}>
                            {quote.account_id ? (
                              <Link
                                to={`/accounts/${quote.account_id}`}
                                className="text-primary hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {quote.account_name || 'N/A'}
                              </Link>
                            ) : (
                              quote.account_name || 'N/A'
                            )}
                          </dd>
                        </div>
                      )
                    })()}

                {renderEditableField(
                  'Quote Owner',
                  'owner_id',
                  quote.owner_name || 'N/A',
                  'owner',
                  undefined,
                  true
                )}

                {renderEditableField(
                  'Expiration Date',
                  'expiration_date',
                  formatDate(quote.expiration_date),
                  'date'
                )}

                {renderEditableField(
                  'Currency',
                  'currency_iso_code',
                  quote.currency_iso_code || 'USD',
                  'text'
                )}

                {/* Issuing region — which GrackleDocs entity appears on the PDF.
                    New quotes always carry one. Quotes created before that change
                    hold a blank region and resolve the entity from the account's
                    country at render time, so they keep the "auto" choice: without
                    it the select would have no option matching their stored value,
                    fall back to showing Canada, and saving ANY field would silently
                    move a live US or Australian quote onto the Canadian entity. */}
                {renderEditableField(
                  'Issuing Region',
                  'region',
                  quoteRegionLabel(quote.region),
                  'select',
                  QUOTE_REGION_OPTIONS,
                  false,
                  quote.region ? null : QUOTE_REGION_AUTO_LABEL
                )}

                {/* Currency / region mismatch — allowed, but must be deliberate. */}
                {isEditMode && regionCurrencyMismatch && (
                  <div
                    className="md:col-span-2 rounded-md border border-destructive/50 bg-destructive/5 p-3"
                    role="alert"
                  >
                    <p className="text-sm font-medium text-destructive">
                      Currency does not match the issuing region
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {regionCurrencyWarning(editForm.region, editForm.currency_iso_code)}
                    </p>
                    <label className="flex items-start gap-2 mt-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={currencyMismatchAck}
                        onChange={(e) => setCurrencyMismatchAck(e.target.checked)}
                        aria-describedby="quote-region-currency-ack"
                      />
                      <span id="quote-region-currency-ack">
                        I understand and want to use{' '}
                        <span className="font-medium">{editForm.currency_iso_code}</span> for this
                        quote.
                      </span>
                    </label>
                  </div>
                )}

                <div className="md:col-span-2">
                  {renderEditableField(
                    'Description',
                    'description',
                    quote.description || 'N/A',
                    'textarea'
                  )}
                </div>
              </dl>

              {/* Save/Cancel buttons when editing */}
              {isEditMode && (
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={updateQuoteMutation.isPending}
                    aria-label="Cancel editing"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdit}
                    disabled={updateQuoteMutation.isPending}
                    aria-label="Save quote changes"
                  >
                    {updateQuoteMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Details</CardTitle>
              <CardDescription>Extended quote information and metadata</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Created Date</dt>
                  <dd className="mt-0.5 text-sm">{formatDateTime(quote.created_date)}</dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Last Modified</dt>
                  <dd className="mt-0.5 text-sm">{formatDateTime(quote.last_modified_date)}</dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Created By</dt>
                  <dd className="mt-0.5 text-sm">{quote.created_by_name || 'N/A'}</dd>
                </div>

                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Last Modified By</dt>
                  <dd className="mt-0.5 text-sm">{quote.last_modified_by_name || 'N/A'}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Related Data Sidebar */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <Tabs defaultValue="lineitems" className="w-full">
            <TabsList className="h-auto flex-wrap gap-1">
              <TabsTrigger value="lineitems">Line Items ({lineItems.length})</TabsTrigger>
              <TabsTrigger value="files">Files ({files.length})</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            {/* Line Items Tab */}
            <TabsContent value="lineitems">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Line Items</CardTitle>
                    <CardDescription>Products and pricing for this quote</CardDescription>
                  </div>
                  {!isAddingLineItem && (
                    <Button
                      onClick={() => setIsAddingLineItem(true)}
                      size="sm"
                      disabled={isLocked}
                      title={
                        isLocked ? `This quote is ${quote.status} and is read-only.` : undefined
                      }
                      aria-label="Add new line item"
                    >
                      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                      Add Item
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add/Edit Line Item Form */}
                  {isAddingLineItem && (
                    <div
                      className="border rounded-lg p-4 space-y-4 bg-muted/20"
                      role="form"
                      aria-label={editingLineItemId ? 'Edit line item' : 'Add new line item'}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm">
                          {editingLineItemId ? 'Edit Line Item' : 'Add New Line Item'}
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={resetLineItemForm}
                          aria-label="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        {/* Product Selection */}
                        <div className="space-y-2">
                          <Label htmlFor="product">Product</Label>
                          <Select
                            value={lineItemForm.product_id}
                            onValueChange={handleProductSelect}
                          >
                            <SelectTrigger
                              id="product"
                              aria-invalid={!!formErrors.product_id}
                              aria-describedby={formErrors.product_id ? 'product-error' : undefined}
                            >
                              <SelectValue placeholder="Select a product" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableProducts.map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.name}{' '}
                                  {product.product_code ? `(${product.product_code})` : ''}
                                  {!product.unit_price && (
                                    <span className="text-muted-foreground">
                                      {' '}
                                      — no {quoteCurrency} price
                                    </span>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {formErrors.product_id && (
                            <p id="product-error" className="text-sm text-destructive" role="alert">
                              {formErrors.product_id}
                            </p>
                          )}
                          {requirePriced && (
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="show-unpriced"
                                checked={showUnpriced}
                                onCheckedChange={(checked) => setShowUnpriced(checked === true)}
                              />
                              <label
                                htmlFor="show-unpriced"
                                className="text-xs text-muted-foreground"
                              >
                                Show products with no {quoteCurrency} price
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Quantity */}
                        <div className="space-y-2">
                          <Label htmlFor="quantity" className="required">
                            Quantity *
                          </Label>
                          <Input
                            id="quantity"
                            type="number"
                            min="1"
                            step="1"
                            value={lineItemForm.quantity || ''}
                            onChange={(e) => {
                              setLineItemForm((prev) => ({
                                ...prev,
                                quantity: Number(e.target.value),
                              }))
                              if (formErrors.quantity) {
                                setFormErrors((prev) => {
                                  const newErrors = { ...prev }
                                  delete newErrors.quantity
                                  return newErrors
                                })
                              }
                            }}
                            aria-required="true"
                            aria-invalid={!!formErrors.quantity}
                            aria-describedby={formErrors.quantity ? 'quantity-error' : undefined}
                          />
                          {formErrors.quantity && (
                            <p
                              id="quantity-error"
                              className="text-sm text-destructive"
                              role="alert"
                            >
                              {formErrors.quantity}
                            </p>
                          )}
                        </div>

                        {/* Unit Price */}
                        <div className="space-y-2">
                          <Label htmlFor="unit_price" className="required">
                            Unit Price *
                          </Label>
                          <Input
                            id="unit_price"
                            type="number"
                            min="0"
                            step="0.01"
                            value={lineItemForm.unit_price || ''}
                            onChange={(e) => {
                              setLineItemForm((prev) => ({
                                ...prev,
                                unit_price: Number(e.target.value),
                              }))
                              if (formErrors.unit_price) {
                                setFormErrors((prev) => {
                                  const newErrors = { ...prev }
                                  delete newErrors.unit_price
                                  return newErrors
                                })
                              }
                            }}
                            aria-required="true"
                            aria-invalid={!!formErrors.unit_price}
                            aria-describedby={
                              [
                                formErrors.unit_price ? 'unit_price-error' : null,
                                unpricedSelection ? 'unit_price-no-list' : null,
                              ]
                                .filter(Boolean)
                                .join(' ') || undefined
                            }
                          />
                          {/* The field pre-fills from the list price, so when there
                              isn't one it silently sits at 0 and the rep has no way
                              to tell that from a genuinely free line. Say so. */}
                          {unpricedSelection && (
                            <NoPriceLabel
                              id="unit_price-no-list"
                              product={unpricedSelection}
                              currency={quoteCurrency}
                            />
                          )}
                          {formErrors.unit_price && (
                            <p
                              id="unit_price-error"
                              className="text-sm text-destructive"
                              role="alert"
                            >
                              {formErrors.unit_price}
                            </p>
                          )}
                        </div>

                        {/* Discount */}
                        <div className="space-y-2">
                          <Label htmlFor="discount">
                            Discount ({quote?.currency_iso_code || 'USD'})
                          </Label>
                          <Input
                            id="discount"
                            type="number"
                            min="0"
                            step="0.01"
                            value={lineItemForm.discount || 0}
                            onChange={(e) => {
                              setLineItemForm((prev) => ({
                                ...prev,
                                discount: Number(e.target.value),
                              }))
                              if (formErrors.discount) {
                                setFormErrors((prev) => {
                                  const newErrors = { ...prev }
                                  delete newErrors.discount
                                  return newErrors
                                })
                              }
                            }}
                            aria-invalid={!!formErrors.discount}
                            aria-describedby={formErrors.discount ? 'discount-error' : undefined}
                          />
                          {formErrors.discount && (
                            <p
                              id="discount-error"
                              className="text-sm text-destructive"
                              role="alert"
                            >
                              {formErrors.discount}
                            </p>
                          )}
                        </div>

                        {/* Product-family-specific required fields */}
                        {(() => {
                          const selectedProduct = availableProducts.find(
                            (p) => p.id === lineItemForm.product_id
                          )
                          const familyKey = selectedProduct
                            ? matchProductFamily({
                                name: selectedProduct.name,
                                family: selectedProduct.family,
                              })
                            : null
                          if (!familyKey) return null
                          const required = REQUIRED_FIELDS_BY_FAMILY[familyKey]
                          const domainLabel = DOMAIN_USERS_LABEL_BY_FAMILY[familyKey]
                          return (
                            <div
                              className="space-y-4 pt-4 border-t"
                              aria-label="Product-specific required fields"
                            >
                              <h4 className="text-sm font-semibold text-muted-foreground">
                                Required for this product
                              </h4>
                              {required.includes('domain_users_text') &&
                                (() => {
                                  const isPdf = familyKey === 'pdf'
                                  const placeholder = isPdf
                                    ? 'alice@acme.com, bob@acme.com'
                                    : undefined
                                  const helpId = 'domain-users-help'
                                  const emailCount = isPdf
                                    ? parseEmailList(lineItemForm.domain_users_text).length
                                    : 0
                                  const qty = Number(lineItemForm.quantity) || 0
                                  const showMismatch =
                                    isPdf && emailCount > 0 && qty > 0 && emailCount !== qty
                                  return (
                                    <div className="space-y-2">
                                      <Label htmlFor="domain_users_text" className="required">
                                        {domainLabel} *
                                      </Label>
                                      <Input
                                        id="domain_users_text"
                                        value={lineItemForm.domain_users_text ?? ''}
                                        placeholder={placeholder}
                                        onChange={(e) => {
                                          setLineItemForm((prev) => ({
                                            ...prev,
                                            domain_users_text: e.target.value,
                                          }))
                                          if (formErrors.domain_users_text) {
                                            setFormErrors((prev) => {
                                              const newErrors = { ...prev }
                                              delete newErrors.domain_users_text
                                              return newErrors
                                            })
                                          }
                                        }}
                                        aria-required="true"
                                        aria-invalid={!!formErrors.domain_users_text}
                                        aria-errormessage={
                                          formErrors.domain_users_text
                                            ? 'domain-users-error'
                                            : undefined
                                        }
                                        aria-describedby={isPdf ? helpId : undefined}
                                        className={
                                          formErrors.domain_users_text ? 'border-destructive' : ''
                                        }
                                      />
                                      {isPdf && (
                                        <p id={helpId} className="text-xs text-muted-foreground">
                                          Comma-separated email addresses, one per user of the
                                          product.
                                        </p>
                                      )}
                                      {showMismatch && (
                                        <p
                                          role="status"
                                          className="text-xs"
                                          style={{ color: SEMANTIC.warning.text }}
                                        >
                                          Heads up: {emailCount} email{emailCount === 1 ? '' : 's'}{' '}
                                          entered for {qty} license{qty === 1 ? '' : 's'}. Please
                                          confirm before saving.
                                        </p>
                                      )}
                                      {formErrors.domain_users_text && (
                                        <p
                                          id="domain-users-error"
                                          role="alert"
                                          className="text-sm text-destructive"
                                        >
                                          {formErrors.domain_users_text}
                                        </p>
                                      )}
                                    </div>
                                  )
                                })()}
                              {required.includes('subscription_term_months') && (
                                <div className="space-y-2">
                                  <Label htmlFor="subscription_term" className="required">
                                    Subscription term *
                                  </Label>
                                  <select
                                    id="subscription_term"
                                    value={lineItemForm.subscription_term_months ?? ''}
                                    onChange={(e) => {
                                      setLineItemForm((prev) => ({
                                        ...prev,
                                        subscription_term_months: e.target.value
                                          ? parseInt(e.target.value, 10)
                                          : null,
                                      }))
                                      if (formErrors.subscription_term_months) {
                                        setFormErrors((prev) => {
                                          const newErrors = { ...prev }
                                          delete newErrors.subscription_term_months
                                          return newErrors
                                        })
                                      }
                                    }}
                                    className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${formErrors.subscription_term_months ? 'border-destructive' : 'border-input'}`}
                                    aria-required="true"
                                    aria-invalid={!!formErrors.subscription_term_months}
                                    aria-errormessage={
                                      formErrors.subscription_term_months
                                        ? 'subscription-term-error'
                                        : undefined
                                    }
                                  >
                                    <option value="">Select...</option>
                                    {SUBSCRIPTION_TERM_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                  {formErrors.subscription_term_months && (
                                    <p
                                      id="subscription-term-error"
                                      role="alert"
                                      className="text-sm text-destructive"
                                    >
                                      {formErrors.subscription_term_months}
                                    </p>
                                  )}
                                </div>
                              )}
                              {required.includes('monthly_delivery_day') && (
                                <div className="space-y-2">
                                  <Label htmlFor="monthly_delivery_day" className="required">
                                    Monthly delivery date *
                                  </Label>
                                  <Input
                                    id="monthly_delivery_day"
                                    type="number"
                                    min={1}
                                    max={31}
                                    value={lineItemForm.monthly_delivery_day ?? ''}
                                    onChange={(e) => {
                                      const raw = e.target.value
                                      setLineItemForm((prev) => ({
                                        ...prev,
                                        monthly_delivery_day: raw ? parseInt(raw, 10) : null,
                                      }))
                                      if (formErrors.monthly_delivery_day) {
                                        setFormErrors((prev) => {
                                          const newErrors = { ...prev }
                                          delete newErrors.monthly_delivery_day
                                          return newErrors
                                        })
                                      }
                                    }}
                                    aria-required="true"
                                    aria-invalid={!!formErrors.monthly_delivery_day}
                                    aria-errormessage={
                                      formErrors.monthly_delivery_day
                                        ? 'monthly-delivery-day-error'
                                        : undefined
                                    }
                                    className={
                                      formErrors.monthly_delivery_day ? 'border-destructive' : ''
                                    }
                                    aria-describedby="monthly-delivery-day-help"
                                  />
                                  <p
                                    id="monthly-delivery-day-help"
                                    className="text-xs text-muted-foreground"
                                  >
                                    Day of the month (1-31)
                                  </p>
                                  {formErrors.monthly_delivery_day && (
                                    <p
                                      id="monthly-delivery-day-error"
                                      role="alert"
                                      className="text-sm text-destructive"
                                    >
                                      {formErrors.monthly_delivery_day}
                                    </p>
                                  )}
                                </div>
                              )}
                              {required.includes('primary_contact_id') && (
                                <div className="space-y-2">
                                  <Label htmlFor="primary_contact" className="required">
                                    Main point of contact *
                                  </Label>
                                  <RecordCombobox
                                    id="primary_contact"
                                    records={accountContacts}
                                    value={lineItemForm.primary_contact_id ?? ''}
                                    onValueChange={(v) => {
                                      setLineItemForm((prev) => ({
                                        ...prev,
                                        primary_contact_id: v || null,
                                      }))
                                      if (formErrors.primary_contact_id) {
                                        setFormErrors((prev) => {
                                          const newErrors = { ...prev }
                                          delete newErrors.primary_contact_id
                                          return newErrors
                                        })
                                      }
                                    }}
                                    displayField={(c: any) =>
                                      c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim()
                                    }
                                    selectedLabel={
                                      accountContacts.find(
                                        (c: any) => c.id === lineItemForm.primary_contact_id
                                      )?.name || undefined
                                    }
                                    placeholder="Select contact..."
                                    aria-label="Main point of contact"
                                    totalCount={contactsData?.total}
                                  />
                                  {formErrors.primary_contact_id && (
                                    <p role="alert" className="text-sm text-destructive">
                                      {formErrors.primary_contact_id}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        })()}
                      </div>

                      {/* Calculated Total */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm font-medium">Line Total:</span>
                        <span className="text-lg font-semibold">
                          {formatCurrency(
                            calculateLineItemTotal(
                              lineItemForm.quantity || 0,
                              lineItemForm.unit_price || 0,
                              lineItemForm.discount || 0
                            )
                          )}
                        </span>
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={resetLineItemForm}
                          disabled={
                            createLineItemMutation.isPending || updateLineItemMutation.isPending
                          }
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={handleSaveLineItem}
                          disabled={
                            createLineItemMutation.isPending || updateLineItemMutation.isPending
                          }
                        >
                          {createLineItemMutation.isPending || updateLineItemMutation.isPending
                            ? 'Saving...'
                            : editingLineItemId
                              ? 'Update'
                              : 'Add'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Line Items Table */}
                  {lineItemsLoading ? (
                    <div
                      className="flex items-center justify-center py-8"
                      role="status"
                      aria-live="polite"
                    >
                      <div
                        className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"
                        role="progressbar"
                        aria-label="Loading line items"
                      ></div>
                      <span className="ml-2 text-sm text-muted-foreground">
                        Loading line items...
                      </span>
                    </div>
                  ) : lineItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 border rounded-lg">
                      <Package
                        className="h-12 w-12 text-muted-foreground mb-4"
                        aria-hidden="true"
                      />
                      <p className="text-sm text-muted-foreground">No line items added yet.</p>
                      {!isAddingLineItem && !isLocked && (
                        <Button
                          onClick={() => setIsAddingLineItem(true)}
                          className="mt-4"
                          variant="outline"
                          aria-label="Add first line item"
                        >
                          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                          Add First Line Item
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="border rounded-md">
                      <table className="w-full text-sm" role="table" aria-label="Quote line items">
                        <thead className="bg-muted/50">
                          <tr>
                            {lineItems.length > 1 && (
                              <th className="text-center p-3 w-20" scope="col">
                                Order
                              </th>
                            )}
                            <th className="text-left p-3" scope="col">
                              Product
                            </th>
                            <th className="text-right p-3" scope="col">
                              Qty
                            </th>
                            <th className="text-right p-3" scope="col">
                              Price
                            </th>
                            <th className="text-right p-3" scope="col">
                              Discount
                            </th>
                            <th className="text-right p-3" scope="col">
                              Total
                            </th>
                            <th className="text-right p-3" scope="col">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <DndContext
                          sensors={dndSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={lineItems.map((item) => item.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <tbody>
                              {lineItems.map((item, index) => (
                                <SortableLineItemRow
                                  key={item.id}
                                  item={item}
                                  index={index}
                                  totalItems={lineItems.length}
                                  isLocked={isLocked}
                                  isAddingLineItem={isAddingLineItem}
                                  reorderPending={reorderLineItemsMutation.isPending}
                                  deletePending={deleteLineItemMutation.isPending}
                                  formatCurrency={formatCurrency}
                                  getLineItemTotal={getLineItemTotal}
                                  onMoveUp={() => handleMoveLineItem(index, 'up')}
                                  onMoveDown={() => handleMoveLineItem(index, 'down')}
                                  onEdit={() => handleEditLineItem(item)}
                                  onDelete={() => handleDeleteLineItem(item.id)}
                                />
                              ))}
                              <tr className="border-t bg-muted/30 font-semibold">
                                <td
                                  colSpan={lineItems.length > 1 ? 5 : 4}
                                  className="text-right p-3"
                                >
                                  Subtotal:
                                </td>
                                <td className="text-right p-3">
                                  {formatCurrency(lineItemsSubtotal)}
                                </td>
                                <td></td>
                              </tr>
                            </tbody>
                          </SortableContext>
                        </DndContext>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="files" className="space-y-4">
              <AttachmentsTab entityId={id!} entityType="Quote" />
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              <HistoryTimeline entityId={id!} entityType="Quote" showFilters={true} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <EditActionBar
        isEditMode={isEditMode}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        isSaving={updateQuoteMutation.isPending}
        saveLabel="Save Changes"
      />
    </div>
  )
}
